import { useEffect, useState, useRef } from 'react';
import { useMap } from 'react-map-gl/maplibre';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { Button } from '@/components/ui/button';
import { 
  CircleGeometry, 
  ZoneGeometry, 
  ZoneType,
  PolygonGeometry
} from '@/api/v0/zones';
import { toast } from 'sonner';
import { createCirclePoints } from '@/lib/geo-utils';

interface ZoneDrawProps {
  onZoneDrawn: (geometry: ZoneGeometry, type: ZoneType) => void;
  initialGeometry?: ZoneGeometry;
}

export function ZoneDraw({ onZoneDrawn, initialGeometry }: ZoneDrawProps) {
  const { current: map } = useMap();
  const drawRef = useRef<any>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Initialize the draw control
  useEffect(() => {
    if (!map) return;
    
    // Clean up any existing draw control
    if (drawRef.current) {
      try {
        map.removeControl(drawRef.current);
        drawRef.current = null;
      } catch {
        return;
      }
    }
    
    // Create a new draw control
    try {
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        }
      });
      
      // Add the control to the map
      map.addControl(draw as any);
      drawRef.current = draw;
      
      // Set up event listeners
      const handleCreate = (e: any) => {
        if (!e.features || e.features.length === 0) return;
        
        const feature = e.features[0];
        
        if (feature.geometry.type === 'Polygon') {
          // Create polygon geometry
          const polygonGeometry: PolygonGeometry = {
            type: 'polygon',
            coordinates: feature.geometry.coordinates
          };
          
          onZoneDrawn(polygonGeometry, 'polygon');
          setIsDrawing(false);
        }
      };
      
      const handleDelete = () => {
        // Handle deletion by sending empty geometry
        onZoneDrawn(
          {
            type: 'polygon',
            coordinates: []
          } as PolygonGeometry,
          'polygon'
        );
      };
      
      map.on('draw.create', handleCreate);
      map.on('draw.update', handleCreate);
      map.on('draw.delete', handleDelete);
      
      // Initialize with existing geometry if provided
      if (initialGeometry && drawRef.current) {
        try {
          if (initialGeometry.type === 'circle') {
            // For circle geometry, create a polygon representation
            const circleGeometry = initialGeometry as CircleGeometry;
            if (circleGeometry.center && circleGeometry.radius > 0) {
              const feature = {
                id: 'initial-zone',
                type: 'Feature' as const,
                properties: {},
                geometry: {
                  type: 'Polygon' as const,
                  coordinates: [createCirclePoints(circleGeometry.center, circleGeometry.radius)]
                }
              };
              
              drawRef.current.add(feature);
            }
          } else if (initialGeometry.type === 'polygon') {
            // For polygon geometry, add it directly
            const polygonGeometry = initialGeometry as PolygonGeometry;
            const feature = {
              id: 'initial-zone',
              type: 'Feature' as const,
              properties: {},
              geometry: {
                type: 'Polygon' as const,
                coordinates: polygonGeometry.coordinates
              }
            };
            
            drawRef.current.add(feature);
          }
        } catch (error) {
          return;
        }
      }
      
      // Return cleanup function
      return () => {
        if (map && drawRef.current) {
          map.off('draw.create', handleCreate);
          map.off('draw.update', handleCreate);
          map.off('draw.delete', handleDelete);
          
          try {
            map.removeControl(drawRef.current);
            drawRef.current = null;
          } catch {
            return;
          }
        }
      };
    } catch {
      return;
    }
  }, [map, onZoneDrawn]);
  
  const startDrawing = () => {
    if (!drawRef.current || !map) {
      toast.error("Drawing tools not initialized");
      return;
    }
    
    // Clean up any existing drawings
    drawRef.current.deleteAll();
    
    // Start drawing
    setIsDrawing(true);
    drawRef.current.changeMode('draw_polygon');
    toast.info("Click points to create a polygon shape. Click the first point again to close the shape.");
  };
  
  const cancelDrawing = () => {
    if (!drawRef.current) return;
    
    drawRef.current.deleteAll();
    drawRef.current.changeMode('simple_select');
    setIsDrawing(false);
  };
  
  return (
    <div className="absolute top-2 left-2 z-10 flex flex-col gap-2" style={{ maxWidth: '200px' }}>
      <div className="flex gap-2">
        <Button 
          onClick={startDrawing} 
          variant={isDrawing ? "secondary" : "default"}
          disabled={isDrawing}
        >
          Draw Polygon
        </Button>
      </div>
      {isDrawing && (
        <div className="flex gap-2">
          <Button onClick={cancelDrawing} variant="destructive">
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
} 